import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { DateTime } from 'luxon';
import * as yup from 'yup';

import { dialog, dom } from 'app/utils';
import { Button } from 'app/components/common';
import { Form } from 'app/components/layout';
import { DateBox, Field, Select } from 'app/components/form';
import { memberships } from 'app/services';
import { IMembership } from 'app/models';

export interface IUpgrade {
  data: IMembership;

  onDone: (data?: IMembership) => void;
}

export default memo(({ data, onDone }: IUpgrade) => {
  const { t } = useTranslation();

  const controller = useFormik<IMembership>({
    initialValues: { expiry: DateTime.now().plus({ month: 1 }).toJSDate(), ...data },
    validationSchema: useMemo(() => yup.object({
      subscriptionId: yup.string().required(),
      expiry: yup.date(),
      status: yup.number().oneOf([0, 1]).required(),
    }), []),

    validateOnBlur: true,
    validateOnChange: false,
    validateOnMount: true,

    onSubmit: () => { },
  }), { handleSubmit, handleReset } = controller;
  const [busy, setBusy] = useState(false);

  const onSave = () => {
    if (!controller.isValid) { dialog.toast({ message: t('app.validation.required'), severity: 'error' }); return; }

    setBusy(true);
    memberships.save(controller.values).then(({ success, data }) => {
      if (!success) return;

      dialog.toast({ severity: 'success', message: t('upgrade.save.success') });
      onDone(data);
    }).finally(() => setBusy(false));
  };

  const { touched, errors } = controller;
  const getProps = useCallback((field: keyof IMembership): any => ({ ...controller.getFieldProps(field),
    invalid: !!(touched[field] && errors[field]),
  }), [controller, touched, errors]);

  const form: any = {
    className: dom.className('block', 'flex-auto', 'overflow-auto'),
    style: { minWidth: '36rem' },

    onSubmit: handleSubmit,
    onReset: handleReset,
  };

  return (
    <>
      <form { ...form }>
        <Form responsive={true}>
          <Field text={t('upgrade.fields.subscription')} error={controller.errors.subscriptionId} align="end" asterisk className="pt-4">
            <Select { ...getProps('subscriptionId') } source={{ type: 'lookup', key: 'subscriptions' }} />
          </Field>
          <Field text={t('upgrade.fields.expiry')} error={controller.errors.expiry} align="end" asterisk className="pt-4">
            <DateBox { ...getProps('expiry') } placeholder={t('upgrade.fields.expiry.hint')} time utc clearable />
          </Field>
        </Form>
      </form>
      <div className="mt-4 flex justify-content-end">
        <Button text label={t('app.cancel')} disabled={busy} className="mr-2" onClick={() => onDone()} />
        <Button label={t('app.save')} disabled={busy} onClick={onSave} />
      </div>
    </>
  );
});
